import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  CASINO_OPTIONS,
  COMPLIANCE_OPTIONS,
  CONFIGURATIONS_OPTIONS,
  GAME_PASS_OPTIONS,
  PAM_TEAM_WHITELIST,
  PAYMENTS_OPTIONS,
  POKER_OPTIONS,
  PREDICTIONS_OPTIONS,
  PROMOTIONS_OPTIONS,
  SIDER_TABS,
  SPORTSBOOK_OPTIONS,
  STAFF_MANAGEMENT_NEW_OPTIONS,
} from "../utils/Constants";
import { ADMIN_TYPES } from "../utils/Constants";
import { NAVIGATION_ROUTES } from "../utils/NavigationUtils";
import {
  AGENT_AUDIT_LOG_ROUTE_PERMISSIONS,
  MAINTENANCE_MODE_ROUTE_PERMISSIONS,
  WITHDRAWAL_REVIEW_ROUTE_PERMISSIONS,
} from "../utils/constants/permissionsConstants";
import { POKER_CONFIGURATION_PERMISSIONS } from "../utils/constants/poker/pokerConfigurationData";
import {
  canAccessGamePassAdmin,
  canAccessGamePassSeasons,
  isGamePassPamEnabled,
} from "../utils/gamePassFeature";
import { TRIVELTA_PRODUCTS } from "../utils/products";
import useIsDevMode from "./useIsDevMode";
import useRegulatorManager from "./useRegulatorManager";
import { useTriveltaProduct } from "./useTriveltaProduct";
import useUserWhitelist from "./useUserWhitelist";
import { STAFF_REGULATORS_SIDER_OPTION } from "../components/StaffRegulators/staffRegulatorsSider";
import CasinoIcon from "../assets/icons/ic_casino.svg";
import ComplianceIcon from "../assets/icons/ic_compliance.svg";
import ConfigurationIcon from "../assets/icons/ic_configuration.svg";
import GamePassIcon from "../assets/icons/ic_game_pass.svg";
import LineChartIcon from "../assets/icons/ic_line-chart.svg";
import PaymentsIcon from "../assets/icons/ic_payments.svg";
import PokerIcon from "../assets/icons/ic_poker.svg";
import PredictionsIcon from "../assets/icons/ic_predictions.svg";
import PromotionsIcon from "../assets/icons/ic_promotion.svg";
import SportsbookIcon from "../assets/icons/ic_sportsbook.svg";
import StaffManagementIcon from "../assets/icons/ic_staff_management.svg";
import UsersIcon from "../assets/icons/ic_users.svg";
import {
  filterSidebarOptions,
  hasAnyPermission,
  hasPermission,
} from "../utils/sider/sidebarAccess";
import { filterVisibleOptions } from "../utils/sider/sidebarVisibility";

const useSidebarSections = ({ adminType, adminPermissions }) => {
  const isWhitelisted = useUserWhitelist();
  const isRegulatorManager = useRegulatorManager();
  const isDevEnv = useIsDevMode();
  const featuresStatusConfig = useSelector(
    (state) => state.config?.appConfig?.features_status_config,
  );
  const { isActive: operationsPlatformEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.OPERATIONS_PLATFORM,
  );
  const { isActive: sportsbookEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.SPORTSBOOK,
    "sportsbook_v2",
  );
  const { isActive: casinoEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.CASINO,
    "casino",
  );
  const { isActive: pokerEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.POKER,
    "poker",
  );
  const { isActive: gamePassProductEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.GAME_PASS,
    "gamePass",
  );
  const { isActive: coreAppEnabled } = useTriveltaProduct(
    TRIVELTA_PRODUCTS.CORE_APP,
  );

  return useMemo(() => {
    const accessContext = {
      adminPermissions,
      isWhitelisted,
      isDevEnv,
    };
    const filterOptions = (options, overrides = {}) =>
      filterSidebarOptions(options, { ...accessContext, ...overrides });
    const canAccessAny = (permissions, options) =>
      hasAnyPermission(adminPermissions, permissions, {
        ...options,
        isDevEnv,
      });
    const isGamePassPamFlagOn =
      isGamePassPamEnabled(featuresStatusConfig);
    const isPamTeamWhitelisted = isWhitelisted(PAM_TEAM_WHITELIST);
    const canSeeGamePassAdmin = canAccessGamePassAdmin({
      featuresStatusConfig,
      adminPermissions,
      isPamTeamWhitelisted,
    });
    const canSeeGamePassSeasons = canAccessGamePassSeasons({
      featuresStatusConfig,
      adminPermissions,
      isPamTeamWhitelisted,
    });

    const paymentsOptions = filterOptions(PAYMENTS_OPTIONS);
    const complianceOptions = filterOptions(
      adminType === ADMIN_TYPES?.COMPLIANCE_AGENT
        ? COMPLIANCE_OPTIONS.filter(
            (option) => option.id === "withdrawal-review",
          )
        : COMPLIANCE_OPTIONS,
    );
    const sportsbookOptions = filterOptions(SPORTSBOOK_OPTIONS);
    const casinoOptions = filterOptions(CASINO_OPTIONS);
    const predictionsOptions = filterOptions(PREDICTIONS_OPTIONS);
    const configurationsOptions = filterOptions(CONFIGURATIONS_OPTIONS);
    const promotionsOptions = filterOptions(PROMOTIONS_OPTIONS);
    const staffOptions = filterOptions([
      ...STAFF_MANAGEMENT_NEW_OPTIONS,
      ...(isRegulatorManager ? [STAFF_REGULATORS_SIDER_OPTION] : []),
    ]);

    return [
      {
        visible:
          canAccessAny([
            "kpis",
            "kpis.overview",
            "kpis.financials",
            "kpis.acquisition",
            "kpis.sportsbook",
            "kpis.casino",
          ]) && operationsPlatformEnabled,
        icon: LineChartIcon,
        tabName: SIDER_TABS?.KPIS,
        labelKey: "PAM_KPIS",
        route: NAVIGATION_ROUTES?.KPIS,
      },
      {
        visible:
          hasPermission(adminPermissions, "users") &&
          operationsPlatformEnabled,
        icon: UsersIcon,
        tabName: SIDER_TABS?.USERS,
        labelKey: "USERS",
        route: NAVIGATION_ROUTES?.USERS,
      },
      {
        visible:
          canAccessAny([
            "sportsbook",
            "sportsbook.configuration.bet_limits",
            "sportsbook.configuration.sgp_limits",
            "sportsbook.configuration.account_config",
            "sportsbook.configuration.early_payout",
            "sportsbook.fieldbook.events_list",
            "sportsbook.monitoring.latest_bets_list",
            "sportsbook.monitoring.sgp_risk",
            "sportsbook.reporting.reporting_summary",
            "sportsbook.reporting.top_users",
          ]) && sportsbookEnabled,
        icon: SportsbookIcon,
        tabName: SIDER_TABS?.SPORTSBOOK,
        labelKey: "SPORTSBOOK",
        options: sportsbookOptions,
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          canAccessAny([
            "casino",
            "casino.reports.finance",
            "casino.reports.providers",
            "casino.reports.games",
            "casino.reports.top_users",
            "casino.configuration",
            "casino.configuration.games",
            "casino.configuration.providers",
            "casino.configuration.collections",
            "casino.configuration.views",
            "casino.configuration.tags",
            "casino.configuration.preview",
            "casino.configuration.features",
            "casino.configuration.pages",
            "casino.configuration.uploads",
          ]) && casinoEnabled,
        icon: CasinoIcon,
        tabName: SIDER_TABS?.CASINO,
        labelKey: "CASINO",
        options: casinoOptions,
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          canAccessAny(
            ["predictions.reports", "predictions.configuration"],
            { developmentMode: true },
          ) && isDevEnv,
        icon: PredictionsIcon,
        tabName: SIDER_TABS?.PREDICTIONS,
        labelKey: "PREDICTIONS",
        options: predictionsOptions,
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          canAccessAny(
            [
              "poker",
              "poker.reporting.top_users",
              ...POKER_CONFIGURATION_PERMISSIONS,
            ],
            { developmentMode: true },
          ) &&
          isDevEnv &&
          pokerEnabled,
        icon: PokerIcon,
        tabName: SIDER_TABS?.POKER,
        labelKey: "POKER",
        options: filterOptions(POKER_OPTIONS),
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible: canSeeGamePassAdmin && gamePassProductEnabled,
        icon: GamePassIcon,
        tabName: SIDER_TABS?.GAME_PASS,
        labelKey: "GAME_PASS",
        options: filterOptions(GAME_PASS_OPTIONS, {
          gamePassPamBypass:
            canSeeGamePassSeasons && isGamePassPamFlagOn,
        }),
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          canAccessAny([
            "promotions.bonus_controls",
            "promotions.affiliates",
            "promotions.utm_reports",
            "promotions.campaigns",
          ]) && operationsPlatformEnabled,
        icon: PromotionsIcon,
        tabName: SIDER_TABS?.PROMOTIONS,
        labelKey: "PAM_PROMOTIONS",
        options: promotionsOptions,
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          (canAccessAny([
            "payments.reports.overview",
            "payments.reports.report_generator",
            "payments.configuration.routing",
            "payments.configuration.delayed_payment",
          ]) ||
            canAccessAny(["payments.configuration.limits"], {
              developmentMode: true,
            })) &&
          operationsPlatformEnabled,
        icon: PaymentsIcon,
        tabName: SIDER_TABS?.PAYMENTS,
        labelKey: "PAYMENTS",
        options: paymentsOptions,
        dropdownMenuClassName: "dropdown-menu--nested",
      },
      {
        visible:
          canAccessAny([
            "compliance",
            "compliance.wallet",
            ...WITHDRAWAL_REVIEW_ROUTE_PERMISSIONS,
            "compliance.fraud_detection",
            ...MAINTENANCE_MODE_ROUTE_PERMISSIONS,
          ]) && operationsPlatformEnabled,
        icon: ComplianceIcon,
        iconClassName: "tab-icon--sider-light",
        tabName: SIDER_TABS?.COMPLIANCE,
        labelKey: "PAM_COMPLIANCE",
        options: complianceOptions,
      },
      {
        visible:
          (canAccessAny([
            "configurations.profile",
            "compliance",
            "casino.scheduled_tasks.read",
          ]) ||
            canAccessAny(["configurations.state_configuration"], {
              developmentMode: true,
            })) &&
          filterVisibleOptions(configurationsOptions).length > 0 &&
          coreAppEnabled,
        icon: ConfigurationIcon,
        tabName: SIDER_TABS?.CONFIGURATIONS,
        labelKey: "PAM_CONFIGURATIONS",
        options: configurationsOptions,
      },
      {
        visible:
          canAccessAny([
            "staff_management",
            "staff_management.staff",
            "staff_management.roles",
            ...AGENT_AUDIT_LOG_ROUTE_PERMISSIONS,
          ]) && operationsPlatformEnabled,
        icon: StaffManagementIcon,
        tabName: SIDER_TABS?.STAFF_MANAGEMENT_NEW,
        labelKey: "STAFF_MANAGEMENT",
        options: staffOptions,
      },
    ].filter(({ visible }) => visible);
  }, [
    adminPermissions,
    adminType,
    casinoEnabled,
    coreAppEnabled,
    featuresStatusConfig,
    gamePassProductEnabled,
    isDevEnv,
    isRegulatorManager,
    isWhitelisted,
    operationsPlatformEnabled,
    pokerEnabled,
    sportsbookEnabled,
  ]);
};

export default useSidebarSections;
